/**
 * Normalize a FastAPI error response into a string safe for a toast or banner.
 * detail is usually a plain string ({"detail": "..."}) but FastAPI's own 422
 * validation errors send an array of {loc, msg} objects, so both are handled
 * (19.2 backend facts only guarantee the string shape, but this keeps other
 * endpoints from rendering "[object Object]").
 */
export function formatApiErrorDetail(detail, fallback = 'Something went wrong') {
  if (detail == null || detail === '') return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const parts = detail.map((entry) => {
      if (typeof entry === 'string') return entry;
      if (entry?.msg) {
        return entry.loc ? `${entry.loc.join('.')}: ${entry.msg}` : entry.msg;
      }
      return JSON.stringify(entry);
    });
    return parts.filter(Boolean).join('; ') || fallback;
  }
  if (typeof detail === 'object' && typeof detail.msg === 'string') return detail.msg;
  return fallback;
}

const SERVER_UNREACHABLE = 'Could not reach the server. Check that the backend is running and try again.';

/**
 * Read a FastAPI axios error (or a network/timeout/gateway failure) into one display
 * string. A FastAPI `detail` always wins when there is one; the status-based branches
 * only cover responses that carry none.
 */
export function getApiErrorMessage(error, fallback = 'Something went wrong') {
  if (!error) return fallback;
  const detail = error.response?.data?.detail;
  if (detail !== undefined && detail !== null) {
    return formatApiErrorDetail(detail, fallback);
  }
  if (error.code === 'ECONNABORTED') {
    return 'Request timed out. Please try again.';
  }
  if (error.message === 'Network Error') {
    return SERVER_UNREACHABLE;
  }
  // QA-31: with the backend container stopped, nginx answers /api/ itself with its stock
  // 502 HTML page, so axios has a response but no detail to read and the message fell
  // through to "Request failed with status code 502". A gateway status with no detail
  // means the backend was never reached -- the same situation as a network failure, so
  // it gets the same sentence.
  const status = error.response?.status;
  if (status === 502 || status === 503 || status === 504) {
    return SERVER_UNREACHABLE;
  }
  return error.message || fallback;
}
